import { isNil } from 'lodash';
import { ValuationApproachModel } from 'api';
import { GRID_NUMBER_CHECKBOX } from 'common/constants/gridType';
import { SELECTION_A, SELECTION_B } from 'common/constants/valuations';
import { SelectValueViewer } from 'components';
import { GridDate, GridDateEditor, GridDateValueViewer, GridSelect } from 'components/FeaturedSpreadsheet/components';
import GridRowLabel from 'components/Grid/GridRowLabel';
import { CellValue, ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { METRICS, TABLE_HEADER_CONFIG } from 'pages/Valuations/approaches/backsolveApproach/constants';
import {
  APPLIED_ADJUSTMENT_ALIAS,
  AS_OF_DATE_ALIAS,
  AS_OF_VALUE_ALIAS,
  AS_OF_VALUE_TITLE,
  KEY_VALUE_PARSE,
  MARKET_ADJUSTMENT_LEDGER_TITLE,
  MD_DATE_ALIAS,
  MD_VALUE_ALIAS,
  MEDIAN_MEDIAN_PARSE,
  METRIC_ALIAS,
  PERCENT_CHANGE_TITLE,
  PERCENTILE_PARSE,
  SELECT_ADJUSTMENT_ALIAS,
  SPECIFIED_ADJUSTMENT_ALIAS,
} from 'pages/Valuations/approaches/backsolveApproach/MarketAdjustmentLedger/util/constants';
import { formulaProps } from 'pages/Valuations/approaches/backsolveApproach/MarketAdjustmentLedger/util/getRowConfig';
import { getPercentileLabel } from 'pages/ValuationsAllocation/util';
import { isFalseStrict } from 'utilities';
import colConfig, { getLedgerValueCustomFormat, getLedgerValueFormat } from './colConfig';
import { DetermineEnabledStatusProps, FinancialMetrics, MdCompType, ParserProps, Row, RowConfig } from './types';

const SUBTITLE_BOLD = 'subtitle-bold';
const SUBTITLE_BOLD_NO_BORDER = `${SUBTITLE_BOLD} no-side-border`;
const SUBTITLE_BOTTOM_BORDER_ONLY = 'division-bottom-only';

function medianMedianParse(row: Row, index: number, cells: Partial<ICell>, rowRange: string) {
  if (row.parseAs === MEDIAN_MEDIAN_PARSE) {
    const rowNumber = index + 1;
    const labelKey = `C${rowNumber}`;
    const valueKey = `D${rowNumber}`;
    // eslint-disable-next-line no-param-reassign
    cells[labelKey as keyof ICell] = {
      ...row,
      customKey: undefined,
      className: '',
      valueViewer: (props: any) => <GridRowLabel {...props} />,
      key: labelKey,
    };
    // eslint-disable-next-line no-param-reassign
    cells[valueKey as keyof ICell] = {
      ...row,
      key: valueKey,
      expr: `=${row.formula}(FILTER_ENABLED([${rowRange}]))`,
    };
  }
}

function percentileParse(row: Row, index: number, cells: Partial<ICell>, rowRange: string) {
  if (row.parseAs === PERCENTILE_PARSE) {
    const rowNumber = index + 1;
    const labelKey = `C${rowNumber}`;
    const valueKey = `D${rowNumber}`;
    const value = row.value || row.defaultValue;
    // eslint-disable-next-line no-param-reassign
    cells[labelKey as keyof ICell] = {
      ...row,
      value: value as CellValue,
      key: labelKey,
      className: 'text-left',
    };

    // eslint-disable-next-line no-param-reassign
    cells[valueKey as keyof ICell] = {
      ...formulaProps,
      key: valueKey,
      alias: row.alias,
      expr: `=PERCENTILE(FILTER_ENABLED([${rowRange}]), ${labelKey})`,
    } as unknown as ICell;
  }
}

function keyValueParse(
  row: Row,
  index: number,
  cells: Partial<ICell>,
  approach: ValuationApproachModel,
  publicComps: any,
  rowRange: string,
  isDisabled: boolean
) {
  if (row.parseAs === KEY_VALUE_PARSE) {
    const rowNumber = index + 1;
    const labelKey = `C${rowNumber}`;
    const valueKey = `D${rowNumber}`;
    const value = row.value || row.defaultValue;
    // eslint-disable-next-line no-param-reassign
    cells[labelKey as keyof ICell] = {
      ...row,
      key: labelKey,
      customKey: undefined,
      valueViewer: (props: any) => <GridRowLabel {...props} />,
    };

    // eslint-disable-next-line no-param-reassign
    cells[valueKey as keyof ICell] = {
      ...row,
      key: labelKey,
      readOnly: isDisabled,
      value: value as CellValue,
    };

    const savedAdjustment = approach.valuations_approach_backsolve?.adjustment_selected;
    const convertToReadableValue = (): CellValue => {
      if ([SELECTION_A, SELECTION_B].includes(savedAdjustment as string)) {
        const percentileNumber =
          savedAdjustment === SELECTION_A
            ? (approach.valuations_approach_backsolve?.percentile_selection_a as number)
            : (approach.valuations_approach_backsolve?.percentile_selection_b as number);
        return getPercentileLabel(Number(percentileNumber - 1));
      }
      return savedAdjustment;
    };

    if (row.alias === SELECT_ADJUSTMENT_ALIAS) {
      // eslint-disable-next-line no-param-reassign
      cells[valueKey as keyof ICell].value = convertToReadableValue() ?? (row.defaultValue as CellValue);
    }

    if (row.alias === SPECIFIED_ADJUSTMENT_ALIAS) {
      // eslint-disable-next-line no-param-reassign
      cells[valueKey as keyof ICell].value =
        approach.valuations_approach_backsolve?.specified_adjustment ?? (row.defaultValue as CellValue);
    }

    if (row.alias === APPLIED_ADJUSTMENT_ALIAS) {
      const selection_obj = {
        '\'Median\'': `D${3 + publicComps.length + 1}`,
        '\'Mean\'': `D${3 + publicComps.length + 2}`,
        [`GET_PERCENTILE_OPTION(C${3 + publicComps.length + 3})`]: `D${3 + publicComps.length + 3}`,
        [`GET_PERCENTILE_OPTION(C${3 + publicComps.length + 4})`]: `D${3 + publicComps.length + 4}`,
        '\'75th Percentile\'': `PERCENTILE(FILTER_ENABLED([${rowRange}]), 75)`,
        '\'25th Percentile\'': `PERCENTILE(FILTER_ENABLED([${rowRange}]), 25)`,
        '\'Specified\'': `D${3 + publicComps.length + 5}`,
      };
      const objExpressionString = Object.entries(selection_obj)
        .map(([key, value]) => `[${key}, ${value}]`)
        .join(',');
      const expr = `=FROM_KEY_VALUE_PAIRS([${objExpressionString}], D${rowNumber - 1})`;
      // eslint-disable-next-line no-param-reassign
      cells[valueKey as keyof ICell].expr = expr;
      // eslint-disable-next-line no-param-reassign
      cells[valueKey as keyof ICell].readOnly = true;
    }
  }
}

export const hasUsableMetricValues = (
  asOfComp: FinancialMetrics,
  mdComp: MdCompType,
  chosenMetric: string
) => {
  const asOfCompsMetricValue = asOfComp[chosenMetric as keyof FinancialMetrics] ?? 0;
  const mdCompsMetricValue = mdComp[chosenMetric as keyof FinancialMetrics] ?? 0;

  // Disable the cell if either value is zero (no data is available)
  if (Number(asOfCompsMetricValue) === 0 || Number(mdCompsMetricValue) === 0) {
    return false;
  }

  // numerator is zero
  if (Number(asOfCompsMetricValue) === Number(mdCompsMetricValue)) {
    return false;
  }
  // based on denominator validation
  return Number(asOfCompsMetricValue) !== 0;
};

// Null is a saved value never fetched, unlike Cap IQ's 0 for "no data"; it asks for a refresh instead
export const isMissingAsOfValue = (asOfComp: FinancialMetrics, chosenMetric: string) =>
  isNil(asOfComp?.[chosenMetric as keyof FinancialMetrics]);

const determineEnabledStatus = ({ gridType, chosenMetric, asOfComp, mdComp, row }: DetermineEnabledStatusProps) => {
  if (gridType !== GRID_NUMBER_CHECKBOX) {
    return undefined;
  }
  if (isFalseStrict(row.enabled)) {
    return false;
  }
  // A missing value keeps the user's choice so the refresh prompt can show; no data unchecks the comp
  return isMissingAsOfValue(asOfComp, chosenMetric) || hasUsableMetricValues(asOfComp, mdComp, chosenMetric);
};

export default async ({ rowConfig, tableData }: ParserProps) => {
  const { isDisabled } = tableData;
  const { publicComps, approach } = tableData;
  const metric = tableData.metric ?? approach.valuations_approach_backsolve.metric;
  const cells: Record<string, Partial<ICell>> = {};
  cells.A1 = {
    ...TABLE_HEADER_CONFIG,
    value: MARKET_ADJUSTMENT_LEDGER_TITLE,
    rowNumber: 1,
    colSpan: 4,
    valueViewer: (props: any) => <GridRowLabel {...props} />,
  };
  cells.A2 = {
    value: AS_OF_VALUE_TITLE,
    className: `${SUBTITLE_BOLD} ${SUBTITLE_BOTTOM_BORDER_ONLY}`,
    rowSpan: 2,
  };
  cells.B2 = {
    value: tableData.asOfDate,
    alias: AS_OF_DATE_ALIAS,
    readOnly: isDisabled,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore: GridDate is not typed
    gridDateComponent: GridDate,
    dataEditor: (props: any) => <GridDateEditor {...props} />,
    valueViewer: (props: any) => <GridDateValueViewer {...props} />,
    className: SUBTITLE_BOLD_NO_BORDER,
  };
  cells.C2 = {
    value: `As of ${tableData.measurementDate}`,
    alias: MD_DATE_ALIAS,
    className: SUBTITLE_BOLD_NO_BORDER,
  };
  cells.D2 = {
    value: PERCENT_CHANGE_TITLE,
    className: SUBTITLE_BOLD_NO_BORDER,
  };
  cells.A3 = {
    value: '',
    className: SUBTITLE_BOTTOM_BORDER_ONLY,
  };
  cells.B3 = {
    value: metric,
    alias: METRIC_ALIAS,
    readOnly: isDisabled,
    ignoreRowCopy: true,
    dbType: 'string',
    gridType: 'string',
    className: `${SUBTITLE_BOTTOM_BORDER_ONLY} ignore-title-column`,
    dataEditor: (props: any) => <GridSelect menuPosition="absolute" enumerated options={METRICS} {...props} />,
    valueViewer: (props: any) => <SelectValueViewer options={METRICS} useLabel {...props} />,
    dropdown: true,
  };
  cells.C3 = {
    expr: '=B3',
    readOnly: true,
    gridType: 'string',
    className: SUBTITLE_BOTTOM_BORDER_ONLY,
  };
  cells.D3 = {
    expr: '=B3',
    readOnly: true,
    gridType: 'string',
    className: SUBTITLE_BOTTOM_BORDER_ONLY,
  };
  const rowRange = publicComps.map((_, j) => `D${j + 3 + 1}`).toString();
  const updatedCells = rowConfig.reduce((cells, row, index) => {
    if (index >= 3 && index < 3 + publicComps.length) {
      const { mdComp, asOfComp } = row;
      const rowCells = colConfig.reduce((sofar, colFn) => {
        const rowNumber = index + 1;
        const col = colFn(row, rowNumber);
        const cell = {
          ...col,
          ...([AS_OF_VALUE_ALIAS, MD_VALUE_ALIAS].includes(col.alias)
            ? {
              format: getLedgerValueFormat(metric as string),
              customFormat: getLedgerValueCustomFormat(metric as string),
            }
            : {}),
          insideLedger: true,
          value: row[col.alias as keyof RowConfig],
          key: col.columnLegend + rowNumber,
          enabled: determineEnabledStatus({
            gridType: col.gridType,
            mdComp,
            asOfComp,
            chosenMetric: metric as string,
            row,
          }),
        } as ICell;
        if (index === 3 + publicComps.length - 1) {
          cell.className = 'division-bottom-only ';
        }
        return { ...sofar, [cell.key as string]: cell };
      }, {});
      return { ...cells, ...rowCells };
    }
    medianMedianParse(row, index, cells, rowRange);
    percentileParse(row, index, cells, rowRange);
    keyValueParse(row, index, cells, approach, publicComps, rowRange, isDisabled);

    return cells;
  }, cells);
  Object.entries(updatedCells).forEach(([key, value]) => {
    // eslint-disable-next-line no-param-reassign
    value.key = key;
  });
  return updatedCells;
};
